import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { submitQuote, submitSample, submitConsultation } from "@/lib/api";

const titles = {
  quote: { t: "Request a Quote", d: "Share your requirement — our team responds within 24 hours." },
  sample: { t: "Request a Sample", d: "Try our products before you order in bulk." },
  consult: { t: "Get a Free Consultation", d: "Talk to our packaging experts — no obligations." },
};

export const QuoteDialog = ({ open, onClose, mode = "quote" }) => {
  const meta = titles[mode] || titles.quote;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    product_category: "Paper Cups", quantity: "", message: "",
    address: "", product_interest: "Paper Cups",
    business_type: "", requirement: "",
  });

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, name: "", company: "", email: "", phone: "", quantity: "", message: "", address: "", business_type: "", requirement: "" }));
    }
    // setForm is stable; intentionally only re-runs when dialog opens or mode changes.
  }, [open, mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone");
      return;
    }
    if (mode === "sample" && !form.address) {
      toast.error("Please share a delivery address for the sample");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "quote") {
        await submitQuote({
          name: form.name, company: form.company, email: form.email,
          phone: form.phone, product_category: form.product_category,
          quantity: form.quantity, message: form.message,
        });
      } else if (mode === "sample") {
        await submitSample({
          name: form.name, company: form.company, email: form.email,
          phone: form.phone, address: form.address,
          product_interest: form.product_interest, notes: form.message,
        });
      } else {
        await submitConsultation({
          name: form.name, email: form.email, phone: form.phone,
          business_type: form.business_type, requirement: form.requirement || form.message,
        });
      }
      toast.success("Thanks! Our team will reach out soon.");
      onClose();
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid={`quote-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{meta.t}</DialogTitle>
          <DialogDescription>{meta.d}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input data-testid="qd-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label className="text-xs">Company</Label>
              <Input data-testid="qd-company" className="mt-1" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input data-testid="qd-email" type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <Label className="text-xs">Phone *</Label>
              <Input data-testid="qd-phone" className="mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </div>
          </div>

          {mode === "quote" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Product Category</Label>
                <Select value={form.product_category} onValueChange={(v) => setForm({ ...form, product_category: v })}>
                  <SelectTrigger className="mt-1" data-testid="qd-product-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paper Cups">Paper Cups</SelectItem>
                    <SelectItem value="Tissue Products">Tissue Products</SelectItem>
                    <SelectItem value="Food Packaging">Food Packaging</SelectItem>
                    <SelectItem value="Custom Branding">Custom Branding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Approx. Quantity</Label>
                <Input data-testid="qd-quantity" className="mt-1" placeholder="e.g. 10000 pcs / month" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
            </div>
          )}

          {mode === "sample" && (
            <>
              <div>
                <Label className="text-xs">Delivery Address *</Label>
                <Textarea data-testid="qd-address" rows={2} className="mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
              </div>
              <div>
                <Label className="text-xs">Product Interest</Label>
                <Select value={form.product_interest} onValueChange={(v) => setForm({ ...form, product_interest: v })}>
                  <SelectTrigger className="mt-1" data-testid="qd-product-interest"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paper Cups">Paper Cups</SelectItem>
                    <SelectItem value="Tissue Products">Tissue Products</SelectItem>
                    <SelectItem value="Food Packaging">Food Packaging</SelectItem>
                    <SelectItem value="Custom Branding">Custom Branding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {mode === "consult" && (
            <div>
              <Label className="text-xs">Business Type</Label>
              <Input data-testid="qd-business" className="mt-1" placeholder="Restaurant, Cafe, Hotel, etc." value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} />
            </div>
          )}

          <div>
            <Label className="text-xs">{mode === "consult" ? "What do you need help with?" : "Message / Notes"}</Label>
            <Textarea data-testid="qd-message" rows={3} className="mt-1" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="qd-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} className="vc-gradient text-white border-0" data-testid="qd-submit">
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
